import { AfterViewInit, Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { distinctUntilChanged, map, Subscription } from 'rxjs';
import { viewAttachements } from '../../new-rep-application-interfaces/attachements.interface';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { NewRepApplicationService } from '../../new-rep-application-services/new-rep-application.service';
import { ConfirmPopupComponent } from '../../confirm-popup/confirm-popup.component';
import { HttpStatusCode } from 'axios';
import { SnackbarService } from '../../../../../shared/services/snackbar/snackbar.service';
import { SessionStorageService } from '../../../../../core/services/session-storage/session-storage.service';
import { ViewNewRepApplication } from '../../new-rep-application-interfaces/viewnew-rep-application.interface';

@Component({
  selector: 'app-additional-attachments',
  imports: [
    ReactiveFormsModule,
    MatInputModule,
    MatFormFieldModule,
    MatIcon,
    MatButtonModule,
    MatTooltipModule
  ],
  templateUrl: './additional-attachments.component.html',
  styleUrl: './additional-attachments.component.scss'
})
export class AdditionalAttachmentsComponent implements OnInit, OnDestroy {

  @Output() next = new EventEmitter<string[]>();
  @Input() formGroup!: FormGroup;
  @Input() viewattachments!: viewAttachements[];
  @Input() readonly = signal(false);
  /**
   * Onboarding id of the application on screen. The applicant is looking at their
   * own record, but a reviewer opens someone else's - so the id has to come from
   * the page rather than from the session, or the read-only view would refetch the
   * reviewer's application and wipe the attachments passed in.
   */
  @Input() viewId!: number;
  @Output() preview = new EventEmitter<string[]>();
  additionalDetails!: ViewNewRepApplication;

  private fb = inject(FormBuilder);
  private readonly matDialog = inject(MatDialog);
  private newRepApplicationService = inject(NewRepApplicationService);
  private sessionStorageService = inject(SessionStorageService);
  private snackBarService = inject(SnackbarService);
  status = 'Not Started';
  readonly message = 'This field is required.';

  /** Prefix used for the dynamically added "Other Attachments" controls. */
  readonly otherAttachmentPrefix = 'otherAttachment';
  /** Document type sent for every "Other Attachments" upload - the row index goes in ResponseValue. */
  readonly otherAttachmentDescription = 'OtherAttachments';
  /** Control keys of the rendered "Other Attachments" rows, e.g. ['otherAttachment1', 'otherAttachment2']. */
  otherAttachmentKeys: string[] = [];
  private otherAttachmentSeq = 0;

  /** Sibling section holding the CM-EIP / M6 and CM-SIP / M6A answers. */
  private readonly qualificationsGroupName = 'regulatoryProfessionalQualifications';
  private qualificationsSubscription?: Subscription;

  async ngOnInit(): Promise<void> {
    this.formGroup.addControl('writtenAuthoritypreviousemployment', this.fb.control([null]));
    this.formGroup.addControl('writtenAuthoritypreviousemploymentfileName', this.fb.control('', [Validators.required]));
    this.formGroup.addControl('creditBureauReport', this.fb.control([null]));
    this.formGroup.addControl('creditBureauReportfileName', this.fb.control('', [Validators.required]));
    this.formGroup.addControl('CPFTransactionHistory', this.fb.control([null]));
    this.formGroup.addControl('CPFTransactionHistoryfileName', this.fb.control('', [Validators.required]));
    this.formGroup.addControl('passportsizedPhoto', this.fb.control([null]));
    this.formGroup.addControl('passportsizedPhotofileName', this.fb.control('', [Validators.required]));
    this.formGroup.addControl('letterofAuthorizationtoCPFBoard', this.fb.control([null]));
    this.formGroup.addControl('letterofAuthorizationtoCPFBoardfileName', this.fb.control('', [Validators.required]));
    this.formGroup.addControl('acknowledgementofTransferRulesForm', this.fb.control([null]));
    this.formGroup.addControl('acknowledgementofTransferRulesFormfileName', this.fb.control('', [Validators.required]));
    this.formGroup.addControl('LetterofUndertakingforRegisterofInterestsinSecurities', this.fb.control([null]));
    this.formGroup.addControl('LetterofUndertakingforRegisterofInterestsinSecuritiesfileName', this.fb.control(''));


    // Awaited so the conditional "Letter of Undertaking" validator is in place
    // before the section status is derived from the form validity below.
    await this.viewDocumants();
    await this.fetchAdditionalAttachments();
    this.buildOtherAttachments();

    this.watchQualificationChanges();
    // Subscribed only once the saved attachments are in place, so the baseline it
    // compares against is the loaded state. Watching from before the load made
    // hydrating a field - and disabling the form for a reviewer - look like an
    // edit, which overwrote the status derived above with "In Progress".
    this.handleFormStatus();

  }

  ngOnDestroy(): void {
    this.qualificationsSubscription?.unsubscribe();
  }

  /** Restores any previously uploaded "Other Attachments" and always keeps at least one empty row. */
  private buildOtherAttachments(): void {
    this.viewattachments?.forEach(item => {
      const index = this.otherAttachmentIndexOf(item);
      if (index !== null) {
        this.restoreOtherAttachment(`${this.otherAttachmentPrefix}${index}`, item.fileName);
      }
    });

    if (this.otherAttachmentKeys.length === 0) {
      this.addOtherAttachment();
    }

    // The API lists the documents in its own order, so the rows are put back in row
    // order here. Left unsorted the fields swap places on every fetch, and because
    // the template hangs the remove button off every row but the first and the add
    // button off the last, those buttons end up on whichever rows came back first.
    this.otherAttachmentKeys.sort((a, b) => this.otherAttachmentRow(a) - this.otherAttachmentRow(b));
  }

  /** True when the control key belongs to an "Other Attachments" row. */
  private isOtherAttachmentKey(key: string): boolean {
    return key.startsWith(this.otherAttachmentPrefix);
  }

  /** The row index sent as ResponseValue for an "Other Attachments" control key. */
  private otherAttachmentIndex(key: string): string {
    return key.slice(this.otherAttachmentPrefix.length);
  }

  /** Row index of a control key as a number, for matching and ordering. */
  private otherAttachmentRow(key: string): number {
    return Number(this.otherAttachmentIndex(key));
  }

  /**
   * Row index an uploaded document belongs to, or null when it isn't an "Other
   * Attachments" row. Uploads carry 'OtherAttachments' as the document type with
   * the index in responseValue; rows saved before that change kept the index in
   * the document type itself, so both shapes are recognised here. The index is
   * read as a number so a row is still matched when the API echoes it back in a
   * different form (' 2', 2) than the one that was sent.
   */
  private otherAttachmentIndexOf(item: viewAttachements | undefined): number | null {
    const docType = item?.onboardingDocumentType ?? '';
    const raw = docType === this.otherAttachmentDescription
      ? String(item?.responseValue ?? '').trim()
      : docType.startsWith(this.otherAttachmentPrefix)
        ? docType.slice(this.otherAttachmentPrefix.length)
        : '';

    const row = Number(raw);
    return raw !== '' && Number.isFinite(row) ? row : null;
  }

  /**
   * Locates the uploaded document behind a control key - "Other Attachments"
   * rows are matched on their index, every other field on its document type.
   * Returns the most recent match, mirroring how the list was read before.
   */
  private findAttachment(fileVar: string): viewAttachements | undefined {
    const matches = this.isOtherAttachmentKey(fileVar)
      ? this.viewattachments?.filter(item => this.otherAttachmentIndexOf(item) === this.otherAttachmentRow(fileVar))
      : this.viewattachments?.filter(item => item?.onboardingDocumentType === fileVar);

    return matches?.[matches.length - 1];
  }

  private restoreOtherAttachment(key: string, fileName: string): void {
    if (!this.otherAttachmentKeys.includes(key)) {
      this.formGroup.addControl(key, this.fb.control([null]));
      this.formGroup.addControl(`${key}fileName`, this.fb.control(''));
      this.otherAttachmentKeys.push(key);

      const seq = Number(key.slice(this.otherAttachmentPrefix.length));
      if (!Number.isNaN(seq) && seq > this.otherAttachmentSeq) {
        this.otherAttachmentSeq = seq;
      }
    }
    this.formGroup.get(`${key}fileName`)?.setValue(fileName);
  }

  /** Duplicates the "Other Attachments" field so another file can be attached. */
  addOtherAttachment(): void {
    const key = `${this.otherAttachmentPrefix}${++this.otherAttachmentSeq}`;
    this.formGroup.addControl(key, this.fb.control([null]));
    this.formGroup.addControl(`${key}fileName`, this.fb.control(''));
    this.otherAttachmentKeys.push(key);
  }

  /** Removes a duplicated "Other Attachments" row, deleting its uploaded file first when there is one. */
  removeOtherAttachment(key: string): void {
    if (!this.formGroup.get(`${key}fileName`)?.value) {
      this.dropOtherAttachment(key);
      return;
    }

    const dialogRef = this.matDialog.open(ConfirmPopupComponent, {
      width: '500px',
      disableClose: true,
      data: {
        message: 'Do you want to remove the attachement?',
        hidecancel: false,
      }
    });
    dialogRef.afterClosed().subscribe(async result => {
      if (result.data === true) {
        // Refreshed here rather than alongside the dialog: the row is matched
        // against the documents as they stand once the user has confirmed, so an
        // upload or delete made in the meantime cannot hand this call a stale id.
        await this.viewDocumants();
        await this.deleteUploadedDocument(key);
        this.dropOtherAttachment(key);
      }
    });
  }

  private async deleteUploadedDocument(fileVar: string): Promise<void> {
    const match = this.findAttachment(fileVar);
    // Nothing stored behind this row - the field only holds a file the user has
    // just picked, or the document has already gone. Sending the call anyway posts
    // undefined ids, which is the request the API rejects.
    if (!match?.id) return;

    const data = {
      onboardingId: match.onboardingId,
      docTypeId: match.docTypeId,
      id: match.id
    }
    await this.newRepApplicationService
      .deletedocument(data)
      .then((response) => {
        return JSON.parse(response);
      })
      .catch((error) => {
        // console.error(error);
        return error;
      });
  }

  private dropOtherAttachment(key: string): void {
    this.formGroup.removeControl(key);
    this.formGroup.removeControl(`${key}fileName`);
    this.otherAttachmentKeys = this.otherAttachmentKeys.filter(item => item !== key);
  }

  fetchAdditionalAttachments(): void {

    // The list was refreshed by viewDocumants(), so the duplicated rows are rebuilt
    // from it here - restoring a row is a no-op when it is already on screen.
    this.buildOtherAttachments();

    this.viewattachments.forEach(item => {
      if (item?.onboardingDocumentType === 'writtenAuthoritypreviousemployment') {
        this.formGroup.controls['writtenAuthoritypreviousemploymentfileName']?.setValue(item?.fileName);
      }
      if (item?.onboardingDocumentType === 'creditBureauReport') {
        this.formGroup.controls['creditBureauReportfileName']?.setValue(item?.fileName);
      }
      if (item?.onboardingDocumentType === 'CPFTransactionHistory') {
        this.formGroup.controls['CPFTransactionHistoryfileName']?.setValue(item?.fileName);
      }
      if (item?.onboardingDocumentType === 'passportsizedPhoto') {
        this.formGroup.controls['passportsizedPhotofileName']?.setValue(item?.fileName);
      }
      if (item?.onboardingDocumentType === 'letterofAuthorizationtoCPFBoard') {
        this.formGroup.controls['letterofAuthorizationtoCPFBoardfileName']?.setValue(item?.fileName);
      }
      if (item?.onboardingDocumentType === 'acknowledgementofTransferRulesForm') {
        this.formGroup.controls['acknowledgementofTransferRulesFormfileName']?.setValue(item?.fileName);
      }
      if (item?.onboardingDocumentType === 'LetterofUndertakingforRegisterofInterestsinSecurities') {
        this.formGroup.controls['LetterofUndertakingforRegisterofInterestsinSecuritiesfileName']?.setValue(item?.fileName);
      }
    })
    if (this.formGroup.valid) {
      this.status = 'Completed';
      this.next.emit([this.status, 'submitted']);
    } else if (this.formGroup.invalid) {
      const anyFilled = Object.values(this.formGroup.value).some(v => v && v.toString().trim() !== '');
      if (anyFilled) {
        this.status = 'In Progress';
        this.next.emit([this.status, 'submitted']);
      } else {
        this.status = 'Not Started';
        this.next.emit([this.status, 'submitted']);
      }
    }
    // Silent: toggling the whole group rewrites formGroup.value (disabled controls
    // drop out of it), which the status watcher would otherwise read as an edit.
    if (this.readonly() == true) {
      this.formGroup?.disable();
    } else {
      this.formGroup?.enable();
    }
  }


  handleFormStatus() {
    if (this.readonly()) return;

    const initialValue = this.formGroup.getRawValue();

    this.formGroup.valueChanges.subscribe(currentValue => {
      const hasChanged =
        JSON.stringify(initialValue) !== JSON.stringify(currentValue);

      if (hasChanged) {
        this.status = 'In Progress';
        this.next.emit([this.status, 'change']);
      }
    });
  }

  onAttachmentFileChange(event: any, controlName: string, fileVar: string): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    const control = this.formGroup.get(controlName);

    // ✅ Valid file → update form properly
    this.formGroup.patchValue({
      [fileVar]: file,
      [controlName]: file?.name
    });

    if (!control) return;

    // Reset first
    control.markAsTouched();
    control.setErrors(null);

    if (!file) {
      control.setValue(null);
      return;
    }

    const allowedTypes = new Set([
      'application/pdf',
      'image/jpeg',
      'image/png'
    ]);

    const maxSize = 5 * 1024 * 1024; // 5MB

    // Validate type
    if (!allowedTypes.has(file.type)) {
      control.setErrors({ invalidType: true });
      return;
    }

    // Validate size
    if (file.size > maxSize) {
      control.setErrors({ maxSize: true });
      return;
    }

    control.updateValueAndValidity();
    this.uploaddocument(fileVar, controlName);
  }

  async uploaddocument(fileVar: string, controlName: string) {

    const formData = new FormData();
    const isOtherAttachment = this.isOtherAttachmentKey(fileVar);

    formData.append('file', this.formGroup.get(fileVar)?.value);
    formData.append('Description', isOtherAttachment ? this.otherAttachmentDescription : fileVar);
    formData.append('filename', this.formGroup.get(fileVar)?.value?.name);
    formData.append('ResponseValue', isOtherAttachment ? this.otherAttachmentIndex(fileVar) : '');

    const responseData = await this.newRepApplicationService
      .uploadDocuments(formData)
      .then((response) => {
        return JSON.parse(response);
      })
      .catch((error) => {
        // console.error(error);
        return error;
      });
    if (responseData.status === HttpStatusCode.Ok) {
      this.formGroup.patchValue({
        [controlName]: responseData.data.responsedata
      });
      this.viewDocumants();
      this.snackBarService.openSuccessSnackBar("document uploaded successfully!");
    } else {
      if (responseData.code === HttpStatusCode.BadRequest) {
        // Display notification on API failure.
        this.snackBarService.openFailureSnackBar("We couldn’t process your request. Please try again");
      }
    }
  }

  async viewDocumants() {
    // Falls back to the session id only for the applicant's own draft, where the
    // page has no separate record to point at.
    let id = Number(this.viewId) || Number(this.sessionStorageService.getCurrentUserId());
    const responseData = await this.newRepApplicationService
      .viewApplicationDetails(id)
      .then((response) => {
        return JSON.parse(response);
      })
      .catch((error) => {
        // console.error(error);
        return error;
      });

    const responsedata = responseData?.data?.responsedata;
    if (responsedata) {
      this.viewattachments = responsedata.attachments;
      this.additionalDetails = responsedata.NewRepData;
    }

    this.applyLetterOfUndertakingRequirement();
  }

  /**
   * The Regulatory & Professional Qualifications section is a sibling form group
   * on the same parent form, so its answers can be read while the applicant is
   * still editing them - before anything has been saved to the server.
   */
  private get qualificationsGroup(): FormGroup | null {
    return this.formGroup.root.get(this.qualificationsGroupName) as FormGroup | null;
  }

  /**
   * Keeps the conditional requirement in step with the M6 / M6A answers. Every
   * section stays mounted behind its expansion panel, so ngOnInit runs only on a
   * full page load - without this the rule would apply only after a refresh.
   */
  private watchQualificationChanges(): void {
    const qualifications = this.qualificationsGroup;
    if (!qualifications) return;

    this.qualificationsSubscription = qualifications.valueChanges
      .pipe(
        map(() => this.isLetterOfUndertakingRequired()),
        distinctUntilChanged()
      )
      .subscribe(() => this.applyLetterOfUndertakingRequirement());
  }

  /**
   * "Letter of Undertaking for Register of Interests in Securities" is mandatory
   * only when CM-EIP / M6 or CM-SIP / M6A is answered Yes in Regulatory &
   * Professional Qualifications - it stays optional for every other answer.
   */
  private isLetterOfUndertakingRequired(): boolean {
    return this.isQualificationYes(this.qualificationAnswer('m6', this.additionalDetails?.m6Rep))
      || this.isQualificationYes(this.qualificationAnswer('m6a', this.additionalDetails?.m6aRep));
  }

  private applyLetterOfUndertakingRequirement(): void {
    const fileControl = this.formGroup.get(
      'LetterofUndertakingforRegisterofInterestsinSecuritiesfileName'
    );
    if (!fileControl) return;

    if (this.isLetterOfUndertakingRequired()) {
      fileControl.setValidators([Validators.required]);
      this.status = 'In Progress';
      this.next.emit([this.status, 'change']);
    } else {
      fileControl.clearValidators();
      if (this.formGroup.valid) {
        this.status = 'Completed';
        this.next.emit([this.status, 'change']);
      }
    }

    // The value is untouched here, so no need to wake the form's valueChanges.
    fileControl.updateValueAndValidity();
  }

  /**
   * The answer currently in play: what the applicant has picked in the
   * qualifications section, falling back to the saved value while that section
   * has not been filled in yet.
   */
  private qualificationAnswer(control: string, persisted: string | undefined): string {
    const live = this.qualificationsGroup?.get(control)?.value;
    return live === null || live === undefined || live === ''
      ? String(persisted ?? '')
      : String(live);
  }

  /**
   * True when a qualification was answered Yes. The section holds the literal
   * 'yes' while it is being edited, but a saved Yes comes back as the
   * qualification's yyyy-MM date - so anything that is neither 'no', 'E'
   * (Exempted) nor blank counts as a Yes.
   */
  private isQualificationYes(value: string | undefined | null): boolean {
    const answer = String(value ?? '').trim();
    return answer !== '' && answer !== 'no' && answer !== 'E';
  }

  removeFile(controlName: string, fileVar: string) {
    const dialogRef = this.matDialog.open(ConfirmPopupComponent, {
      width: '500px',
      disableClose: true,
      data: {
        message: 'Do you want to remove the attachement?',
        hidecancel: false,
      }
    });
    dialogRef.afterClosed().subscribe(async result => {
      if (result.data === true) {
        // Same ordering as removing a whole row: refresh, then match, then delete.
        await this.viewDocumants();
        await this.deleteUploadedDocument(fileVar);

        this.formGroup.get(controlName)?.reset();
        const fileInput = document.getElementById(controlName) as HTMLInputElement;
        if (fileInput) {
          fileInput.value = '';
        }
        this.formGroup.get(fileVar)?.reset();
        this.formGroup.controls[controlName].markAsTouched();
        this.formGroup.controls[controlName].updateValueAndValidity();
      } else {
        // console.log('User cancelled');
      }
    });
  }

  async downloadForm(index: number) {
    let url = '';
    let filename = '';

    if (index == 1) {
      url = '/sample_documents/consent_to_conduct_reference_check_vmay2026.pdf';
      filename = 'consent_to_conduct_reference_check_vmay2026.pdf';
    } else if (index == 2) {
      url = '/sample_documents/form_cpf_medisave_authorisation_form_vjan2026.pdf';
      filename = 'form_cpf_medisave_authorisation_form_vjan2026.pdf';
    } else if (index == 3) {
      url = '/sample_documents/acknowledgement_of_transfer_rules_form_vmay2026.pdf';
      filename = 'acknowledgement_of_transfer_rules_form_vmay2026.pdf';
    } else if (index == 4) {
      url = '/sample_documents/letter_of_undertaking_for_dealing_in_securities_bonds_vmay2026.pdf';
      filename = 'letter_of_undertaking_for_dealing_in_securities_bonds_vmay2026.pdf';
    }
    const data = {
      filepath: url,
      filename: filename,

    }

    await this.newRepApplicationService.downloadForms(data)

  }

  saveAndContinue() {

    // Save API call or form validation here
    if (this.formGroup.valid) {
      this.status = 'Completed';

    } else {
      this.status = 'In Progress';
    }
    this.formGroup.markAllAsTouched();
    this.next.emit([this.status, 'submit']);
  }

  previewAttachment(controlName: string, categoryName: string) {
    if (this.viewattachments) {
      const id = this.findAttachment(categoryName)?.id;
      let filename = this.formGroup.get(controlName)?.value;
      this.preview.emit([String(id ?? ''), filename]);
    }
  }

}
